import { useState, useMemo } from 'react';
import type { StageOwner } from '@recruitai/shared';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Search } from 'lucide-react';

interface OrgUser {
  userId: string;
  name: string;
  email: string;
  role: string;
}

interface StageOwnerSelectorProps {
  owners: StageOwner[];
  orgUsers: OrgUser[];
  onChange: (owners: StageOwner[]) => void;
}

export function StageOwnerSelector({ owners, orgUsers, onChange }: StageOwnerSelectorProps) {
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const eligibleUsers = useMemo(() => {
    const ownerIds = new Set(owners.map(o => o.userId));
    return orgUsers
      .filter(u => ['owner', 'admin', 'recruiter'].includes(u.role))
      .filter(u => !ownerIds.has(u.userId))
      .filter(u => {
        if (!search) return true;
        const q = search.toLowerCase();
        return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
      });
  }, [orgUsers, owners, search]);

  const addOwner = (user: OrgUser) => {
    onChange([...owners, { userId: user.userId, userName: user.name, contextualRole: '' }]);
    setSearch('');
    setShowDropdown(false);
  };

  const removeOwner = (userId: string) => {
    onChange(owners.filter(o => o.userId !== userId));
  };

  const updateRole = (userId: string, contextualRole: string) => {
    onChange(owners.map(o => o.userId === userId ? { ...o, contextualRole } : o));
  };

  return (
    <div className="space-y-2">
      {owners.map(owner => (
        <div key={owner.userId} className="flex items-center gap-2 p-2 bg-muted rounded-md">
          <Badge variant="secondary" className="shrink-0">{owner.userName}</Badge>
          <Input
            value={owner.contextualRole}
            onChange={e => updateRole(owner.userId, e.target.value)}
            placeholder="Papel (ex: Entrevistador Técnico)"
            className="h-8 text-sm"
          />
          <Button type="button" variant="ghost" size="sm" onClick={() => removeOwner(owner.userId)}
            aria-label={`Remover ${owner.userName}`}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}

      <div className="relative">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => { setSearch(e.target.value); setShowDropdown(true); }}
            onFocus={() => setShowDropdown(true)}
            placeholder="Buscar usuário para adicionar..."
            className="h-8 text-sm"
          />
        </div>
        {showDropdown && eligibleUsers.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-background border rounded-md shadow-lg max-h-40 overflow-y-auto">
            {eligibleUsers.slice(0, 10).map(user => (
              <button
                key={user.userId}
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center justify-between"
                onClick={() => addOwner(user)}
              >
                <span>{user.name}</span>
                <span className="text-muted-foreground text-xs">{user.email}</span>
              </button>
            ))}
          </div>
        )}
        {showDropdown && eligibleUsers.length === 0 && search && (
          <div className="absolute z-10 mt-1 w-full bg-background border rounded-md shadow-lg p-3 text-sm text-muted-foreground">
            Nenhum usuário encontrado
          </div>
        )}
      </div>
    </div>
  );
}
